'use client';

import React from 'react';
import styled, { useTheme } from 'styled-components';

export interface ProgressBarThresholds {
  warn?: number;
  danger?: number;
}

export interface ProgressBarProps {
  value: number;
  max: number;
  thresholds?: ProgressBarThresholds;
  width?: number;
  height?: number;
}

const Track = styled.span<{ $width?: number; $height?: number }>`
  display: inline-block;
  width: ${(p) => p.$width ?? 28}px;
  height: ${(p) => p.$height ?? 3}px;
  background: ${(p) => p.theme.colors.neutral.line};
  border-radius: ${(p) => p.theme.radius.sm};
  overflow: hidden;
  vertical-align: middle;
`;

const Fill = styled.span<{ $pct: number; $color: string }>`
  display: block;
  width: ${(p) => p.$pct}%;
  height: 100%;
  background: ${(p) => p.$color};
  transition: width ${(p) => p.theme.transition.layout};
`;

export function ProgressBar({ value, max, thresholds, width, height }: ProgressBarProps) {
  const theme = useTheme();
  const clamped = Math.max(0, Math.min(value, max));
  const pct = max > 0 ? (clamped / max) * 100 : 0;
  const ratio = max > 0 ? clamped / max : 0;
  const warnAt = thresholds?.warn ?? 0.6;
  const dangerAt = thresholds?.danger ?? 0.85;

  let color = theme.colors.semantic.success.color;
  if (ratio > dangerAt) color = theme.colors.semantic.error.color;
  else if (ratio > warnAt) color = theme.colors.semantic.warn.color;

  return (
    <Track
      role="progressbar"
      aria-valuenow={clamped}
      aria-valuemin={0}
      aria-valuemax={max}
      $width={width}
      $height={height}
    >
      <Fill data-fill $pct={pct} $color={color} />
    </Track>
  );
}
